import { Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { filterRecords, getInitialFilterState } from "../../../common/filters/filterState";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import type { ViewType } from "../../../common/ViewSelector";
import ViewSelector from "../../../common/ViewSelector";
import FundsCardList from "./FundsCardList";
import { filterDefinitions, searchDefinition } from "./fundsFilters";
import { getFundPath } from "./fundsGridDataProvider";
import FundsGrid from "./FundsGrid";

const FundStructureFundsPage = () => {
  usePageTitle("Funds");

  const { clientCode } = useClientContext();
  const navigate = useNavigate();

  const [viewType, setViewType] = useState<ViewType>("List");

  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState(`${clientCode}_funds_filter_v1`, filterDefinitions, [], [], searchDefinition)
  );

  const getFunds = useCallback(() => adminApi.searchFunds({ fieldIds: ["Stage"], includeInvestorCount: true }), []);

  const [fundsResp, error, { isFetching }] = useFetch(getFunds);

  const navigateToFund = useCallback(
    (fundId: string) => {
      navigate(getFundPath(fundId));
    },
    [navigate]
  );

  if (error) {
    logError(error, "FundStructureFundsPage");
    return <DataLoadingFailed title="Loading funds" />;
  }

  const funds = fundsResp?.items ?? [];
  const filteredFunds = filterRecords(filterState, funds).sort((a, b) => a.name.localeCompare(b.name));
  const isLoading = isFetching || fundsResp === undefined;

  return (
    <>
      <GeneralPageHeader title="Funds" />
      <FilterContextProvider filterState={filterState} setFilterState={setFilterState} allRowsForSelectOptions={funds}>
        <Stack spacing={2} sx={{ height: "100%", px: 3, pb: 1, pt: 2.5 }}>
          <FiltersPanel totalCount={funds.length} recordCount={filteredFunds.length} isLoading={isLoading}>
            <ViewSelector view={viewType} onChange={setViewType} />
          </FiltersPanel>
          {viewType === "List" && <FundsGrid funds={filteredFunds} isLoading={isLoading} />}
          {viewType === "Grid" && (
            <FundsCardList onFundClick={navigateToFund} funds={filteredFunds} isLoading={isLoading} />
          )}
        </Stack>
      </FilterContextProvider>
    </>
  );
};

export default FundStructureFundsPage;
